import React from 'react';
import {
  useListAgentRunsQuery,
  useTriggerAgentRunMutation,
} from '@/redux/internal-services/agentsApi';
import { SectionCard, RunStatusPill, relTime, fmtDuration } from './agentShared';

const TRIGGER_LABELS: Record<string, string> = {
  manual: 'Manual — triggered on demand',
  schedule: 'Schedule — runs on a cron schedule',
  webhook: 'Webhook — triggered by an incoming HTTP call',
  event: 'Event — triggered by a platform event',
};

interface WorkflowTabProps {
  agentId: string;
  triggerType?: string;
}

const WorkflowTab: React.FC<WorkflowTabProps> = ({ agentId, triggerType }) => {
  const { data: runsData, isLoading: runsLoading, refetch: refetchRuns } = useListAgentRunsQuery(agentId);
  const [triggerRun, { isLoading: triggering }] = useTriggerAgentRunMutation();
  const runs = runsData?.data ?? [];

  const handleTrigger = async () => {
    await triggerRun(agentId).unwrap().catch(() => {});
    refetchRuns();
  };

  return (
    <>
      <SectionCard title="Trigger configuration" desc="How this workflow agent is started.">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-ui-raised border border-ui-border">
          <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
            <svg className="h-4 w-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-ui-text">
              {triggerType ? (TRIGGER_LABELS[triggerType] ?? triggerType) : 'No trigger type set'}
            </div>
            <p className="text-xs text-ui-text3 mt-0.5">Configure trigger type in the Overview tab.</p>
          </div>
          {triggerType === 'manual' && (
            <button
              type="button"
              onClick={handleTrigger}
              disabled={triggering}
              className="ml-auto shrink-0 bg-brand-dark text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark/90 transition disabled:opacity-50"
            >
              {triggering ? 'Triggering…' : 'Trigger Now'}
            </button>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Recent runs" desc="Last executions of this workflow agent.">
        {runsLoading ? (
          <div className="py-8 text-center text-sm text-ui-text3">Loading runs…</div>
        ) : runs.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-ui-text3">No runs yet.</p>
            {triggerType === 'manual' && (
              <button
                type="button"
                onClick={handleTrigger}
                disabled={triggering}
                className="mt-3 text-sm text-brand-dark font-medium hover:underline"
              >
                Trigger the first run →
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ui-border text-left">
                  <th className="pb-2 font-medium text-ui-text2 text-xs pr-4">Run ID</th>
                  <th className="pb-2 font-medium text-ui-text2 text-xs pr-4">Status</th>
                  <th className="pb-2 font-medium text-ui-text2 text-xs pr-4">Started</th>
                  <th className="pb-2 font-medium text-ui-text2 text-xs pr-4">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ui-border">
                {runs.map((r) => (
                  <tr key={r.run_id}>
                    <td className="py-2.5 pr-4">
                      <span className="font-mono text-xs text-ui-text2">{r.run_id.slice(0, 12)}…</span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <RunStatusPill status={r.status} />
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-ui-text2">
                      {r.started_at ? relTime(r.started_at) : '—'}
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-ui-text2">
                      {fmtDuration(r.duration_seconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </>
  );
};

export default WorkflowTab;
